import axios from 'axios';
import { getApiUrl } from '../utils/config';
import type {
  Cards,
  ChargebeeCustomer,
  Invoice,
  Subscription,
} from '../utils/types';
import { choiceApiClient } from './axiosClient';

// Customer
export const getCustomerByPcode = async (
  pCode: string,
): Promise<ChargebeeCustomer | null> => {
  try {
    const url = getApiUrl('choice', 'customer', { pcode: pCode });
    const response = await choiceApiClient.get(url);
    return response.data;
  } catch (error) {
    // Handle 404 as "customer not found" - return null instead of throwing
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    // Check if error message indicates 404 (from our error interceptor)
    if (error instanceof Error && error.message.includes('not found')) {
      return null;
    }
    throw error;
  }
};

export const createCustomerForPcode = async (
  pCode: string,
  data: ChargebeeCustomer,
): Promise<void> => {
  const url = getApiUrl('choice', 'customer', { pcode: pCode });
  await choiceApiClient.post(url, data);
};

// Subscription
export const getSubscriptionByPcode = async (
  pCode: string,
): Promise<Subscription | null> => {
  const url = getApiUrl('choice', 'subscriptions', { pcode: pCode });
  const response = await choiceApiClient.get(url);
  return response.data;
};

export const cancelSubscription = async (siteId: string, reason?: string): Promise<void> => {
  let url = getApiUrl('choice', 'cancelSubscription', {
    siteId: siteId,
    status: 'cancelled',
  });
  if (reason) {
    url += `?reason=${reason}`;
  }
  await choiceApiClient.post(url);
};

export const createSubscription = async (siteId: string): Promise<void> => {
  const url = getApiUrl('choice', 'createSubscription', { siteId: siteId });
  const response = await choiceApiClient.post(url);
  if (response.status !== 200) {
    throw new Error(response.data.message);
  }

  const { redirectUrl } = response.data;
  if (redirectUrl) {
    window.location.href = redirectUrl;
  }

  return response.data;
};

export const getCardsByPcode = async (pCode: string): Promise<Cards[]> => {
  const url = getApiUrl('choice', 'cards', { pcode: pCode });
  const response = await choiceApiClient.get(url);
  return response.data;
};

// Invoices
export const getInvoicesByPcode = async (pCode: string): Promise<Invoice[]> => {
  const url = getApiUrl('choice', 'invoices', { pcode: pCode });
  const response = await choiceApiClient.get(url);
  return response.data;
};

// Download Invoice
// /site-subscription/invoice/{invoiceId}/download
export const downloadInvoice = async (invoiceId: string): Promise<void> => {
  const url = getApiUrl('choice', 'downloadInvoice', { invoiceId: invoiceId });
  const response = await choiceApiClient.get(url);
  return response.data;
}; 

// Remove card 
export const removeCard = async (pcode: string, cardId: string): Promise<void> => {
  const url = getApiUrl('choice', 'removeCard', { pcode: pcode, cardId: cardId });
  await choiceApiClient.delete(url);
};

// Update customer billing details
export const updateCustomerBillingDetails = async (pcode: string, data: any): Promise<void> => {
  const url = getApiUrl('choice', 'updateCustomerBillingDetails', { pcode: pcode });
  await choiceApiClient.post(url, data);
};